import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Edit, FileText, Lock, Plus, Search, Trash2 } from 'lucide-react';
import type { ListPagesQuery, PageSummary } from '@news-cms/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { SuperAdminFallback } from '@/components/auth/super-admin-fallback';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { pagesListQueryOptions, useDeletePage } from '@/lib/pages';

interface PagesSearch {
  q?: string;
  page?: number;
}

export const Route = createFileRoute('/_authenticated/pages/')({
  validateSearch: (search: Record<string, unknown>): PagesSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
  }),
  component: PagesListPage,
});

function PagesListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const canManage = hasPermission(user, 'settings.update');

  const filter: Partial<ListPagesQuery> = {
    q: search.q,
    page: search.page ?? 1,
    pageSize: 20,
  };
  const query = useQuery(pagesListQueryOptions(filter));
  const deleteMutation = useDeletePage();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [pendingDelete, setPendingDelete] = useState<PageSummary | null>(null);

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const currentPage = filter.page ?? 1;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { ...search, q: searchInput.trim() || undefined, page: 1 } });
  };
  const goPage = (page: number) => {
    void navigate({ search: { ...search, page } });
  };

  if (!user || user.role.slug !== 'super_admin') return <SuperAdminFallback />;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <FileText className="h-6 w-6 text-slate-400" /> Pages
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              จัดการหน้าต่างๆ ของเว็บ public — แต่ละ Page เก็บ layout (slots → Group Content) และ
              public site ดึงผ่าน <code className="text-xs">/api/public/pages/:slug</code>
            </p>
          </div>
          {canManage && (
            <Link to="/pages/new" className={buttonVariants({ variant: 'default' })}>
              <Plus className="mr-1 h-4 w-4" /> สร้าง Page
            </Link>
          )}
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="ค้นหา name หรือ slug…"
              className="pl-8"
            />
          </div>
          <Button type="submit" variant="secondary">
            ค้นหา
          </Button>
        </form>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">ชื่อ / slug</th>
                <th className="px-4 py-2 text-right font-medium">Slots</th>
                <th className="px-4 py-2 font-medium">อัปเดต</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {query.isLoading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                    กำลังโหลด…
                  </td>
                </tr>
              )}
              {!query.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                    {search.q ? 'ไม่พบ Page ที่ตรง filter' : 'ยังไม่มี Page'}
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {item.isSystem && (
                        <Lock className="h-3 w-3 text-slate-400" aria-label="system page" />
                      )}
                      <span className="font-medium text-slate-900 dark:text-slate-50">
                        {item.name}
                      </span>
                    </div>
                    <code className="text-xs text-slate-400">/pages/{item.slug}</code>
                    {item.description && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">
                        {item.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">
                    {item.slotCount}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(item.updatedAt).toLocaleString('th-TH', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to="/pages/$id/edit"
                        params={{ id: item.id }}
                        className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                        title="แก้ไข"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      {canManage && !item.isSystem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDelete(item)}
                          title="ลบ"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm">
            <div className="text-slate-500">
              {total} รายการ — หน้า {currentPage}/{totalPages}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => goPage(currentPage - 1)}
              >
                ก่อนหน้า
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => goPage(currentPage + 1)}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="ลบ Page?"
        description={
          pendingDelete
            ? `จะลบ "${pendingDelete.name}" (/${pendingDelete.slug}) — public ที่ขอ URL นี้จะได้ 404 ทันที`
            : ''
        }
        confirmLabel="ลบ"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}
